import { useEffect, useRef, useState, type RefObject } from 'react';
import { Video, AudioLines, FileText, File, Image as ImageIcon, Play } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useVideoThumbnail } from '@/components/shakeApp/useVideoThumbnail';
import type { NormalizedItem } from './types';

interface ThumbnailProps {
  item: NormalizedItem;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
}

const sizeClasses = {
  sm: 'h-10 w-10',
  md: 'h-20 w-20',
  lg: 'h-32 w-full',
};

const iconSizes = {
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-12 w-12',
};

// Play badge over video thumbnails: circle size, then the icon inside it
const playBadgeSizes = {
  sm: ['h-5 w-5', 'h-2.5 w-2.5'],
  md: ['h-8 w-8', 'h-4 w-4'],
  lg: ['h-10 w-10', 'h-5 w-5'],
};

export function Thumbnail({ item, size = 'md', className, onClick }: ThumbnailProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [imgError, setImgError] = useState(false);
  // Project file lists return thumbnailUrl/snapshotUrl; folder listings return the same
  // preview as previewImages[0] (image thumbnail, or the video/PDF snapshot). For images,
  // fall back to the file's own signed URL and let the browser downscale it.
  const storedSrc =
    item.thumbnailUrl ||
    item.snapshotUrl ||
    firstPreviewImage(item.previewImages) ||
    (item.type === 'image' ? item.signedUrl : undefined);
  // Videos without any stored snapshot (e.g. robot-produced files) get a frame grabbed in
  // the browser, but only once the card is near the viewport so long lists don't load every video.
  const needsFrame = item.type === 'video' && !storedSrc && !!item.signedUrl;
  const inView = useInView(ref, needsFrame);
  const frame = useVideoThumbnail(needsFrame && inView ? item.signedUrl : null);
  const thumbnailSrc = storedSrc || frame || undefined;
  const hasImage = thumbnailSrc && !imgError;

  const Icon = getTypeIcon(item.type || item.itemtype);
  const iconColor = getTypeColor(item.type || item.itemtype);

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex items-center justify-center rounded bg-secondary',
        hasImage && 'overflow-hidden',
        sizeClasses[size],
        onClick && 'cursor-pointer',
        className,
      )}
      onClick={onClick}
    >
      {hasImage ? (
        <>
          <img
            src={thumbnailSrc}
            alt={item.title}
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
          {item.type === 'video' && (
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute inset-0 m-auto flex items-center justify-center rounded-full bg-black/45 text-white',
                playBadgeSizes[size][0],
              )}
            >
              <Play className={cn('translate-x-[6%] fill-current', playBadgeSizes[size][1])} />
            </span>
          )}
        </>
      ) : (
        <Icon className={cn(iconSizes[size], iconColor)} />
      )}
    </div>
  );
}

/** First preview image URL from a raw `previewImages` list ({S: url} objects or plain strings). */
function firstPreviewImage(previewImages?: unknown[]): string | undefined {
  const first = previewImages?.[0] as { S?: string } | string | undefined;
  if (!first) return undefined;
  return typeof first === 'string' ? first : first.S || undefined;
}

/** True once the element has come within 200px of the viewport. Observes only while `enabled`. */
function useInView(ref: RefObject<HTMLElement | null>, enabled: boolean): boolean {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!enabled || inView || !el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled, inView]);

  return inView;
}

function getTypeIcon(type?: string) {
  switch (type) {
    case 'video': return Video;
    case 'audio': return AudioLines;
    case 'image': return ImageIcon;
    case 'doc':
    case 'document':
    case 'pdf': return FileText;
    default: return File;
  }
}

function getTypeColor(type?: string) {
  switch (type) {
    case 'video': return 'text-blue-500';
    case 'audio': return 'text-purple-500';
    case 'image': return 'text-emerald-500';
    case 'doc':
    case 'document':
    case 'pdf': return 'text-orange-500';
    default: return 'text-muted-foreground';
  }
}
